import { css } from 'styled-components';

import { CareerIconName } from 'components/atoms/CareerIcon/CareerIcon';

/**
 * Цветовые акценты разделов карьерного центра (доработка 0071, 15.09).
 *
 * Владелец на стенде: «разнообразить иконками, графическими элементами, с
 * акцентами, выделением цветом» — одинаковые серые плашки не различались.
 * Каждому разделу — свой тон: светлая заливка-плашка и тёмный знак/текст того
 * же тона. Один раздел — один цвет на всех экранах: пункт меню, плитка этапа,
 * карточка шага и значок урока говорят одним цветом.
 *
 * 🔴 Цвет — ФОН плашки. Смысл по-прежнему несёт подпись и знак: статус
 * «только цветом» не передаётся (ТЗ 10). Жёлтый бренда (`Colors.main`) здесь не
 * используется как цвет текста — у плана своя светло-жёлтая плашка с тёмным
 * знаком. Контраст закреплён тестом `careerAccents.test.ts`.
 *
 * Значения едут CSS-переменными с парой светлая/тёмная — тем же приёмом, что
 * заливки статусов на карте (`CareerSkillMap/skillStatus.ts`): литерал светлой
 * плашки в тёмной теме дал бы светлое пятно со светлым текстом.
 */
export type CareerAccent = 'plan' | 'learning' | 'skills' | 'work' | 'profile' | 'achievements';

export const CAREER_ACCENTS: CareerAccent[] = [
  'plan',
  'learning',
  'skills',
  'work',
  'profile',
  'achievements',
];

export interface IAccentPair {
  /** Заливка плашки. */
  bg: string;
  /** Знак и текст на плашке. */
  fg: string;
}

export const ACCENT_LIGHT: Record<CareerAccent, IAccentPair> = {
  plan: { bg: '#FFF1A8', fg: '#3F3300' },
  learning: { bg: '#E0ECFF', fg: '#1E40AF' },
  skills: { bg: '#EDE5FF', fg: '#5B21B6' },
  work: { bg: '#D9F4E6', fg: '#065F46' },
  profile: { bg: '#E2EAF1', fg: '#27445A' },
  achievements: { bg: '#FFE6CC', fg: '#92400E' },
};

export const ACCENT_DARK: Record<CareerAccent, IAccentPair> = {
  plan: { bg: '#3A3208', fg: '#FFE58A' },
  learning: { bg: '#16284A', fg: '#A9C8FF' },
  skills: { bg: '#2A1F47', fg: '#CDB8FF' },
  work: { bg: '#0F3326', fg: '#9BE3C0' },
  profile: { bg: '#1E2B36', fg: '#B9CCDC' },
  achievements: { bg: '#3B250F', fg: '#FFC98F' },
};

/** Знак раздела — одно место, чтобы меню, плитки и заголовки не разошлись. */
export const ACCENT_ICON: Record<CareerAccent, CareerIconName> = {
  plan: 'compass',
  learning: 'learning',
  skills: 'skills',
  work: 'briefcase',
  profile: 'profile',
  achievements: 'medal',
};

const vars = (map: Record<CareerAccent, IAccentPair>) =>
  CAREER_ACCENTS.map(
    (name) =>
      `--career-accent-${name}-bg: ${map[name].bg}; --career-accent-${name}-fg: ${map[name].fg};`,
  ).join('\n');

/** Объявление переменных — на корне раздела (`CareerLayout`). */
export const careerAccentVars = css`
  ${vars(ACCENT_LIGHT)}

  [data-theme='dark'] & {
    ${vars(ACCENT_DARK)}
  }
`;

export const accent = (name: CareerAccent): IAccentPair => ({
  bg: `var(--career-accent-${name}-bg, ${ACCENT_LIGHT[name].bg})`,
  fg: `var(--career-accent-${name}-fg, ${ACCENT_LIGHT[name].fg})`,
});
